import { useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Navigation } from "lucide-react";
import { fmtShort } from "@/lib/datetime";

const STATUS_COLOR = { tagged: "#F59E0B", booted: "#EF4444", paid: "#FCD34D", removed: "#10B981" };
const iconCache = {};
const pinIcon = (status) => {
  const c = STATUS_COLOR[status] || STATUS_COLOR.booted;
  if (!iconCache[c]) {
    iconCache[c] = L.divIcon({
      className: "",
      html: `<div style="width:18px;height:18px;border-radius:50%;background:${c};border:3px solid #fff;box-shadow:0 0 0 6px ${c}59"></div>`,
      iconSize: [18, 18],
      iconAnchor: [9, 9],
    });
  }
  return iconCache[c];
};

export const parseCoords = (v) => {
  if (v.lat != null && v.lng != null) return [v.lat, v.lng];
  const m = (v.location || "").match(/(-?\d{1,2}\.\d{3,}),\s*(-?\d{1,3}\.\d{3,})/);
  return m ? [parseFloat(m[1]), parseFloat(m[2])] : null;
};

export const distanceMiles = (a, b) => {
  const R = 3958.8, toR = (d) => (d * Math.PI) / 180;
  const dLat = toR(b[0] - a[0]), dLng = toR(b[1] - a[1]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toR(a[0])) * Math.cos(toR(b[0])) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

function FitBounds({ points }) {
  const map = useMap();
  useMemo(() => {
    if (points.length === 1) map.setView(points[0], 16);
    else if (points.length > 1) map.fitBounds(L.latLngBounds(points), { padding: [40, 40] });
  }, [map, points]);
  return null;
}

export default function BootMap({ vehicles, emptyLabel = "No active boots to map.", noGpsLabel = "Active boots have no GPS coordinates yet — enforcers can tap “Use GPS” when tagging.", showLegend = false, testId = "boot-map" }) {
  const shown = vehicles.filter((v) => v.status !== "not_booted");
  const pins = shown.map((v) => ({ v, c: parseCoords(v) })).filter((p) => p.c);
  const points = pins.map((p) => p.c);
  const noGps = shown.length - pins.length;

  if (pins.length === 0) {
    return (
      <div className="h-72 rounded-lg border border-[#28364F] bg-[#0B0F17] flex items-center justify-center text-sm text-[#94A3B8] text-center p-4" data-testid={`${testId}-empty`}>
        {shown.length === 0 ? emptyLabel : noGpsLabel}
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="h-80 rounded-lg overflow-hidden border border-[#28364F]">
        <MapContainer center={points[0]} zoom={14} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
          <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <FitBounds points={points} />
          {pins.map(({ v, c }) => (
            <Marker key={v.id} position={c} icon={pinIcon(v.status)}>
              <Popup>
                <div style={{ fontFamily: "monospace", fontWeight: 700 }}>{v.plate_number} <span style={{ fontSize: 10, color: STATUS_COLOR[v.status] }}>{v.status.toUpperCase()}</span></div>
                <div style={{ fontSize: 12 }}>{v.location}</div>
                <div style={{ fontSize: 11, color: "#666" }}>
                  {v.status === "tagged" ? `Tagged ${fmtShort(v.tagged_at)}` : `Booted ${fmtShort(v.booted_at)} by ${v.enforcer_name}`}
                </div>
                <a href={`https://www.google.com/maps/dir/?api=1&destination=${c[0]},${c[1]}`} target="_blank" rel="noreferrer"
                  style={{ fontSize: 12, fontWeight: 600 }} data-testid={`navigate-${v.plate_number}`}>Navigate →</a>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
      <div className="flex items-center justify-between text-xs text-[#94A3B8] flex-wrap gap-2">
        <span>{pins.length} vehicle{pins.length === 1 ? "" : "s"} on map{noGps > 0 ? ` · ${noGps} without GPS` : ""}</span>
        {showLegend && (
          <span className="flex items-center gap-3" data-testid="map-legend">
            {Object.entries(STATUS_COLOR).map(([s, c]) => (
              <span key={s} className="inline-flex items-center gap-1 uppercase tracking-wider text-[10px]">
                <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: c }} /> {s}
              </span>
            ))}
          </span>
        )}
        {pins.length > 1 && (
          <a className="inline-flex items-center gap-1 text-cyan-400 hover:text-cyan-300" target="_blank" rel="noreferrer"
            data-testid="route-all-link"
            href={`https://www.google.com/maps/dir/${points.map((p) => p.join(",")).join("/")}`}>
            <Navigation className="w-3.5 h-3.5" /> Route through all
          </a>
        )}
      </div>
    </div>
  );
}
